import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service, SourcesMap } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { uniq } from 'webapp/utils/common';

import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonImageSliderComponent from 'webapp/components/common/image-slider';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type BoardRecord from 'webapp/models/board';

export default class BoardLoopsPopupPromoComponent extends PopupInnerModalComponent<PopupData.BoardLoop.Promo> {
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked isLoading = true;

	origin = 'promote_loop_popup';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.LOOP_PROMO_SHOWN);
		void this.userSettingsService.save(UserSettingsKey.LOOP_PROMO_POPUP_SEEN, true);

		void this.mmTemplatesService.requestBoardPublicTemplates().then(() => {
			this.isLoading = false;

			next(() => {
				this.scheduleReposition();
			});
		});
	}

	get sourceModels() {
		return this.mmSourcesService.connectedCloudSourceModels;
	}

	get sourceTypeAvatars() {
		return uniq(this.sourceModels.map((source) => source.sourceType.avatar));
	}

	get popularTemplateImages() {
		return this.popularTemplatesFromConnectedSources.map((template) => template.image);
	}

	@cached
	get popularTemplatesFromConnectedSources() {
		const usedTemplatesIds = uniq(
			this.mmBoardsService.boardModels.map((board) => board.fromTemplate),
		);

		const connectedSourceTypesKeys = this.sourceModels.map((source) => source.sourceType.key);
		return this.mmTemplatesService.popularBoardPublicTemplateModels
			.filter((template) => {
				return template.specificSourceTypesKeys.some((key) =>
					connectedSourceTypesKeys.includes(key),
				);
			})
			.filter((template) => !usedTemplatesIds.includes(template.numericId))
			.slice(0, 3);
	}

	@action
	createLoop() {
		const promises: Promise<BoardRecord | null>[] = [];
		this.popularTemplatesFromConnectedSources.forEach((template) => {
			const sourcesMap: SourcesMap = {};

			template.sources.forEach((sourceTypeId) => {
				const source = this.sourceModels.find(
					(source) => source.sourceType.id === sourceTypeId,
				);
				if (source) {
					sourcesMap[sourceTypeId] = source.id!;
				}
			});

			promises.push(
				this.mmBoardsService.requestCreateBoardFromTemplate(
					template,
					{ dataSourceTypeMap: sourcesMap },
					this.origin,
				) as Promise<BoardRecord | null>,
			);
		});

		void Promise.all(promises).then((boardRecords) => {
			const boardLoop = this.mmBoardLoopsService.createNewModel({
				// boards that failed to create are left out, so the loop is created with the rest
				boardIds: boardRecords.flatMap((boardRecord) =>
					boardRecord?.id ? [Number(boardRecord.id)] : [],
				),
			});
			this.analyticsService.event(TrackingEvent.LOOP_CREATED, { origin: this.origin });

			void boardLoop.save().then(() => {
				this.close();
				// so we have time to close the popup
				later(() => {
					this.mmBoardLoopsService.openPreviewPopup(boardLoop);
				}, 300);
			});
		});
	}
	<template>
		<div class="title title--vertical">
			<CommonUiIconComponent @name="Loop" @size="lg" />
			<span class="text">Access this dashboard package used by companies like yours!</span>
			<span class="subtitle">
				Companies that have connected the same sources are monitoring these dashboards in a
				continuous loop.<br />
				Click below to get this looped dashboard.
			</span>

		</div>
		<div class="content">
			<div class="source-type__list">
				{{#each this.sourceTypeAvatars as |avatarUrl|}}
					<img class="source-type__avatar" src={{avatarUrl}} alt="Avatar" />
				{{/each}}
			</div>

			{{#if this.isLoading}}
				<div class="loader__wrapper">
					<BarLoaderComponent />
				</div>
			{{else}}
				<div class="image-slider-container">
					<CommonImageSliderComponent @images={{this.popularTemplateImages}} />
				</div>
			{{/if}}

			<div class="actions">
				<SubmitButtonComponent
					@text="View dashboards"
					@size="big"
					@onTrigger={{this.createLoop}}
					@submitInProgress={{this.submitInProgress}}
				/>
			</div>

		</div>
	</template>
}
